import {
  Box,
  FormSelect,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@husqvarna/ui-core";
import { useEffect, useRef, useCallback, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";
import debounce from "lodash/debounce";
import { RootState } from "../../shared/store";
import { useForm } from "react-hook-form";
import ServiceYearCosts from "../components/Services/ServiceCostPerYear";
import ProposalFinancingForm from "../components/proposal/ProposalFinancingForm";
import { ProposalFinancingEquipmentOverview } from "../components/proposal/ProposalFinancingEquipmentOverview";
import FormTextWithSuffix from "../../shared/components/FormTextWithSuffix";
import Project from "../models/Project";
import { useSaveProjectFinanceMutation } from "../projectApi";
import { PricingFinanceModel } from "../models/PricingFinanceModel";
import PageContiner from "../../shared/components/PageContainer/PageContainer";
import "./Finance.css";

const createDefaultValues = (project: Project | undefined) => {
  if (!project) {
    return {} as PricingFinanceModel;
  }

  const formModel: PricingFinanceModel = {
    projectId: project.id,
    proposalId: project.proposal.id,
    financingOption: project.proposal.financingOption,
    financingInfo: {
      discount: project.proposal.discount,
      discountUnit: project.proposal.discountUnit,
    },
    leasingInfo: {
      leasingPeriod: project.proposal.leasingPeriod,
      leasingCost: project.proposal.leasingCost,
    },
    installationInfo: {
      installationMaterialCost: project.proposal.installationMaterialCost,
      installationLabourCost: project.proposal.installationLabourCost,
      totalMowersRestValue: project.proposal.totalMowersRestValue,
      depreciationYear: project.proposal.depreciationYears,
    },
    referenceStationRestValue: project.proposal.referenceStationRestValue,
    roboticsMaintenanceCost: project.proposal.roboticsMaintenanceCost,
    serviceInfo: {
      serviceUnit: project.proposal.serviceCostUnit,
      serviceYearCosts: project.proposal.serviceCostPerYear,
    },
  };
  return formModel;
};

const Finance = () => {
  const intl = useIntl();
  const project = useSelector(
    (state: RootState) => state.project.selectedProject,
  );
  const {
    register,
    watch,
    formState: { errors, isDirty, isValid },
    setValue,
    reset,
  } = useForm<PricingFinanceModel>({
    defaultValues: createDefaultValues(project),
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const formValues = watch();

  useEffect(() => {
    if (!project || isDirty) return;
    reset(createDefaultValues(project));
  }, [project, reset]);

  const formRef = useRef<HTMLFormElement>(null);
  const [saveFinanceInfo] = useSaveProjectFinanceMutation();
  const isSavingRef = useRef(false);
  const isValidRef = useRef(isValid);
  isValidRef.current = isValid;

  const saveFunction = useCallback(
    (model: PricingFinanceModel) => {
      if (isSavingRef.current || !isValidRef.current) return;
      isSavingRef.current = true;

      const cleanModel: PricingFinanceModel = {
        projectId: model.projectId,
        proposalId: model.proposalId,
        financingOption: model.financingOption,
        financingInfo: {
          discount: model.financingInfo.discount,
          discountUnit: model.financingInfo.discountUnit,
        },
        leasingInfo: {
          leasingPeriod: model.leasingInfo.leasingPeriod,
          leasingCost: model.leasingInfo.leasingCost,
        },
        installationInfo: {
          installationMaterialCost:
            model.installationInfo.installationMaterialCost,
          installationLabourCost: model.installationInfo.installationLabourCost,
          totalMowersRestValue: model.installationInfo.totalMowersRestValue,
          depreciationYear: model.installationInfo.depreciationYear,
        },
        referenceStationRestValue: model.referenceStationRestValue,
        roboticsMaintenanceCost: model.roboticsMaintenanceCost,
        serviceInfo: {
          serviceUnit: model.serviceInfo.serviceUnit,
          serviceYearCosts: model.serviceInfo.serviceYearCosts,
        },
      };

      saveFinanceInfo(cleanModel)
        .unwrap()
        .then(() => {
          reset(model);
        })
        .catch((err) => {
          console.error("Save failed:", err);
        })
        .finally(() => {
          isSavingRef.current = false;
        });
    },
    [saveFinanceInfo, reset],
  );

  const debouncedSave = useMemo(
    () => debounce(saveFunction, 750),
    [saveFunction],
  );

  // Watch for form changes and trigger autosave
  useEffect(() => {
    if (isDirty && isValid && !isSavingRef.current) {
      debouncedSave(formValues as PricingFinanceModel);
    }
  }, [formValues, isDirty, isValid, debouncedSave]);

  // Cancel debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSave.cancel();
    };
  }, [debouncedSave]);

  const getReferenceStationString = () => {
    const referenceStationNames = project?.proposal.referenceStations
      ?.map((refStation) => {
        return `${refStation.quantity ?? 0}x ${refStation.product.name}`;
      })
      .join(", ");
    return referenceStationNames ? `${referenceStationNames}` : undefined;
  };

  return (
    <PageContiner
      headline={intl.formatMessage({
        id: "projects.finance.heading",
        defaultMessage: "Pricing & Financing",
      })}
      description={intl.formatMessage({
        id: "projects.finance.description",
        defaultMessage:
          "Enter the price and cost details you require to prepare an offer for your client, then export the offer to an editable PowerPoint file where you can make the final adjustments.",
      })}
    >
      <Box container flexDirection="column">
        <form autoComplete="off" ref={formRef}>
          <Box container flexDirection="column" height="auto" gap={3}>
            <Box>
              <Typography tag="h2" variant="Subtitle1" mb={6}>
                {intl.formatMessage({
                  id: "projects.finance.equipmentPrice",
                  defaultMessage: "Equipment price",
                })}
              </Typography>

              <Grid container spacing={4}>
                <Grid item sm={12} md={6} lg={6}>
                  <Box pr={[0, 8]}>
                    {project && project.proposal && (
                      <ProposalFinancingForm
                        proposal={project.proposal}
                        currency={project.region.currency}
                        register={register}
                        setValue={setValue}
                        watch={watch}
                        errors={errors}
                      />
                    )}
                  </Box>
                </Grid>

                <Grid item sm={12} md={6} lg={6}>
                  <Box
                    className="border-left"
                    height="full"
                    pl={[0, 10, 10]}
                    mt={[10, 0, 0]}
                  >
                    {project && (
                      <ProposalFinancingEquipmentOverview
                        proposal={project.proposal}
                        projectId={project.id}
                        proposalId={project.proposal.id}
                        countryCode={project.region.countryCode}
                        currency={project.region.currency}
                      />
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Box>

            <Box>
              <Typography
                tag="h2"
                variant="Subtitle1"
                mb={8}
                mt={12}
                text={intl.formatMessage({
                  id: "projects.finance.installationService",
                  defaultMessage: "Installation & service costs",
                })}
              />

              <Grid container spacing={6}>
                <Grid item sm={12} md={6}>
                  <Box flexDirection="column" flexGrow={1}>
                    <Typography
                      tag="label"
                      htmlFor="installation-material"
                      variant="Body2"
                      className="tcsp-medium"
                      mb={2}
                    >
                      {intl.formatMessage({
                        id: "projects.finance.materialCost",
                        defaultMessage: "Installation material cost",
                      })}
                    </Typography>
                    <FormTextWithSuffix
                      id="installation-material"
                      {...register("installationInfo.installationMaterialCost")}
                      value={watch("installationInfo.installationMaterialCost")}
                      placeholder={intl.formatMessage({
                        id: "general.amout",
                        defaultMessage: "Amount",
                      })}
                      suffix={project?.region.currency}
                      error={
                        errors.installationInfo?.installationMaterialCost
                          ?.message
                      }
                      allowDecimals={false}
                    />
                  </Box>
                </Grid>
                <Grid item sm={12} md={6}>
                  <Box flexDirection="column" flexGrow={1}>
                    <Typography
                      tag="label"
                      htmlFor="installation-labour"
                      variant="Body2"
                      className="tcsp-medium"
                      mb={2}
                    >
                      {intl.formatMessage({
                        id: "projects.finance.labourCost",
                        defaultMessage: "Installation labour cost",
                      })}
                    </Typography>
                    <FormTextWithSuffix
                      id="installation-labour"
                      {...register("installationInfo.installationLabourCost")}
                      value={watch("installationInfo.installationLabourCost")}
                      placeholder={intl.formatMessage({
                        id: "general.amout",
                        defaultMessage: "Amount",
                      })}
                      suffix={project?.region.currency}
                      error={
                        errors.installationInfo?.installationLabourCost?.message
                      }
                      allowDecimals={false}
                    />
                  </Box>
                </Grid>
                <Grid item sm={12} md={6}>
                  <Box flexDirection="column">
                    <Box container alignItems="center" mb={2} height={6}>
                      <Typography
                        tag="label"
                        htmlFor="machine-rest-value"
                        variant="Body2"
                        className="tcsp-medium"
                      >
                        {intl.formatMessage({
                          id: "projects.finance.restValue",
                          defaultMessage: "Rest of value",
                        })}
                      </Typography>
                      <Tooltip
                        toolTipText={intl.formatMessage({
                          id: "equipment.mowerDialog.restOfValueTooltip",
                          defaultMessage:
                            "After depreciation period, based on % of total equipment investment cost",
                        })}
                      >
                        <IconButton
                          icon="info"
                          size="Medium"
                          className="rest-of-value-tooltip"
                        />
                      </Tooltip>
                    </Box>

                    <FormTextWithSuffix
                      id="machine-rest-value"
                      {...register("installationInfo.totalMowersRestValue", {
                        validate: (value) => {
                          if (value === null || value === undefined || String(value) === "") return true;
                          const num = Number(value);
                          if (isNaN(num)) return intl.formatMessage({
                            id: "general.formValidation.notNumber",
                            defaultMessage: "Needs to be a number",
                          });
                          if (num < 0 || num > 100) return intl.formatMessage({
                            id: "general.formValidation.percentageRange",
                            defaultMessage: "Needs to be between 0 and 100",
                          });
                          return true;
                        },
                      })}
                      value={watch("installationInfo.totalMowersRestValue")}
                      suffix="%"
                      placeholder={intl.formatMessage({
                        id: "general.amout",
                        defaultMessage: "Amount",
                      })}
                      error={
                        errors.installationInfo?.totalMowersRestValue?.message
                      }
                      allowDecimals={false}
                    />
                  </Box>
                </Grid>
                <Grid item sm={12} md={6}>
                  <Box flexDirection="column">
                    <Box height={6} mb={2}>
                      <Typography
                        tag="label"
                        variant="Body2"
                        className="tcsp-medium"
                      >
                        {intl.formatMessage({
                          id: "projects.finance.depreciationYear",
                          defaultMessage: "Depreciation year",
                        })}
                      </Typography>
                    </Box>
                    <FormSelect
                      {...register("installationInfo.depreciationYear")}
                      options={Array.from({ length: 8 }, (_, i) => ({
                        label: (i + 1).toString(),
                        value: i + 1,
                      }))}
                      label=""
                      selectedValue={
                        watch("installationInfo.depreciationYear") || undefined
                      }
                      onChangeListener={(value: any) => {
                        const year =
                          typeof value === "object" && value !== null
                            ? value.value
                            : value;
                        setValue(
                          "installationInfo.depreciationYear",
                          Number(year),
                          { shouldDirty: true },
                        );
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>

              <Box mt={12}>
                <Typography tag="p" variant="Subtitle2" mb={4}>
                  {intl.formatMessage({
                    id: "projects.finance.roboticsMaintenanceCost",
                    defaultMessage: "Robotic consumables cost",
                  })}
                </Typography>
                <Typography
                  tag="p"
                  variant="Body2"
                  className="tcsp-medium"
                  mb={2}
                >
                  <FormattedMessage
                    id="projects.finance.roboticsMaintenanceCostInfo"
                    defaultMessage="Monthly costs for blades and other consumables"
                  />
                </Typography>
                <Grid container spacing={6}>
                  <Grid item sm={12} md={6}>
                    <FormTextWithSuffix
                      suffix={project?.region.currency}
                      {...register("roboticsMaintenanceCost", {
                        pattern: {
                          value: /^\d+(\.\d+)?$/,
                          message: intl.formatMessage({
                            id: "general.formValidation.notDecimal",
                            defaultMessage:
                              "Needs to be a number or decimal (with period as decimal sign).",
                            description: "Form error message for not a decimal",
                          }),
                        },
                      })}
                      placeholder={intl.formatMessage({
                        id: "general.amout",
                        defaultMessage: "Amount",
                      })}
                      value={watch("roboticsMaintenanceCost")}
                      error={errors.roboticsMaintenanceCost?.message}
                      allowDecimals={true}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Box mt={12}>
                {/* Reference stations */}
                <Typography tag="p" variant="Subtitle2" mb={4}>
                  {intl.formatMessage({
                    id: "projects.finance.referencestations",
                    defaultMessage: "Reference stations",
                  })}
                </Typography>

                <Grid container spacing={8}>
                  <Grid item md={6} sm={12}>
                    <Typography
                      tag="p"
                      variant="Body2"
                      className="tcsp-medium"
                      mb={2}
                    >
                      {intl.formatMessage(
                        {
                          id: "projects.finance.restValue",
                          defaultMessage:
                            "Rest of value",
                        }
                      )}
                    </Typography>
                    <FormTextWithSuffix
                      {...register("referenceStationRestValue", {
                        validate: (value) => {
                          if (value === null || value === undefined || String(value) === "") return true;
                          const num = Number(value);
                          if (isNaN(num)) return intl.formatMessage({
                            id: "general.formValidation.notNumber",
                            defaultMessage: "Needs to be a number",
                          });
                          if (num < 0 || num > 100) return intl.formatMessage({
                            id: "general.formValidation.percentageRange",
                            defaultMessage: "Needs to be between 0 and 100",
                          });
                          return true;
                        },
                      })}
                      value={watch("referenceStationRestValue")}
                      suffix="%"
                      placeholder={intl.formatMessage({
                        id: "general.amout",
                        defaultMessage: "Amount",
                      })}
                      error={errors.referenceStationRestValue?.message}
                      allowDecimals={false}
                      maxLength={3}
                      disabled={
                        (project?.proposal.referenceStations?.length ?? 0) === 0
                      }
                    />
                  </Grid>
                  <Grid item sm={12} md={6} >
                    <Box flexDirection='row' height='full' alignItems='flex-end'>
                      <Typography
                      mb={3}
                      tag="p"
                      variant="Body2"
                      className="tcsp-medium"
                      text={getReferenceStationString()}
                    /></Box>
                  </Grid>
                </Grid>
              </Box>
              <Box mt={8}>
                {project && (
                  <ServiceYearCosts
                    errors={errors}
                    register={register}
                    setValue={setValue}
                    currency={project.region.currency}
                    costsFieldPath="serviceInfo.serviceYearCosts"
                    unitFieldPath="serviceInfo.serviceUnit"
                  />
                )}
              </Box>
            </Box>
          </Box>
        </form>
      </Box>
    </PageContiner>
  );
};

export default Finance;
